import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, Instagram, Facebook, Mail, Phone, Globe, LogIn } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const WaIcon = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.5 14.4c-.3-.1-1.8-.9-2-1-.3-.1-.5-.1-.7.1-.2.3-.8 1-.9 1.2-.2.2-.3.2-.6.1-.3-.1-1.3-.5-2.4-1.5-.9-.8-1.5-1.8-1.7-2.1-.2-.3 0-.5.1-.6l.4-.5c.2-.2.2-.3.3-.5.1-.2 0-.4 0-.5l-.9-2.2c-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.5s1.1 2.9 1.2 3.1c.1.2 2.1 3.2 5.1 4.5.7.3 1.3.5 1.7.6.7.2 1.4.2 1.9.1.6-.1 1.8-.7 2-1.4.2-.7.2-1.3.2-1.4-.1-.2-.3-.3-.6-.4zM12 2C6.5 2 2 6.5 2 12c0 1.8.5 3.5 1.3 5L2 22l5.2-1.3c1.4.8 3.1 1.2 4.8 1.2 5.5 0 10-4.5 10-10S17.5 2 12 2zm0 18.2c-1.6 0-3.1-.4-4.4-1.2l-.3-.2-3.1.8.8-3-.2-.3C4 15 3.7 13.5 3.7 12c0-4.6 3.7-8.3 8.3-8.3s8.3 3.7 8.3 8.3-3.7 8.2-8.3 8.2z" />
  </svg>
);

export const WaButton = ({ href, label = "Chat on WhatsApp", testid, className = "" }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testid}
    className={`inline-flex items-center gap-2 rounded-full bg-wa text-ink font-semibold px-6 py-3.5 hover:bg-[#1fbd5a] active:scale-[0.98] transition-[background-color,transform] duration-200 ${className}`}>
    <WaIcon /> {label}
  </a>
);

const LANGS = [["en", "English"], ["id", "Bahasa Indonesia"], ["zh-CN", "中文"], ["es", "Español"], ["fr", "Français"],
  ["ja", "日本語"], ["it", "Italiano"], ["de", "Deutsch"], ["ko", "한국어"], ["nl", "Nederlands"]];

const currentLang = () => (document.cookie.match(/googtrans=\/en\/([^;]+)/) || [])[1] || "en";

const setGoogCookie = (lang) => {
  const parts = window.location.hostname.split(".");
  const domains = [""];
  for (let i = 0; i < parts.length - 1; i++) domains.push(`; domain=.${parts.slice(i).join(".")}`);
  domains.push(`; domain=${window.location.hostname}`);
  const exp = "; expires=Thu, 01 Jan 1970 00:00:00 GMT";
  domains.forEach((d) => { document.cookie = `googtrans=; path=/${d}${exp}`; });
  if (lang !== "en") document.cookie = `googtrans=/en/${lang}; path=/`;
};

const TranslatePicker = () => {
  const [lang, setLang] = useState(currentLang);
  useEffect(() => {
    if (window.__gtLoaded) return;
    window.__gtLoaded = true;
    window.googleTranslateElementInit = () => {
      new window.google.translate.TranslateElement(
        { pageLanguage: "en", includedLanguages: LANGS.map((l) => l[0]).join(","), autoDisplay: false }, "google_translate_element");
    };
    const s = document.createElement("script");
    s.src = "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
    document.body.appendChild(s);
  }, []);
  const change = (v) => {
    setLang(v);
    setGoogCookie(v);
    const combo = document.querySelector(".goog-te-combo");
    if (v !== "en" && combo) {
      combo.value = v;
      combo.dispatchEvent(new Event("change"));
    } else {
      window.location.reload();
    }
  };
  return (
    <div className="notranslate" translate="no">
      <div id="google_translate_element" className="absolute w-px h-px overflow-hidden opacity-0 pointer-events-none" aria-hidden="true" />
      <Select value={lang} onValueChange={change}>
        <SelectTrigger className="h-10 w-auto min-w-[130px] gap-2 rounded-full bg-white text-sm" data-testid="language-picker" aria-label="Language">
          <Globe className="w-4 h-4 text-slate-500 shrink-0" /><SelectValue />
        </SelectTrigger>
        <SelectContent className="notranslate" translate="no">
          {LANGS.map(([code, label]) => <SelectItem key={code} value={code} data-testid={`language-option-${code}`}>{label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
};

const navPath = (slug) => (slug === "home" ? "/" : `/${slug}`);

const Header = ({ site }) => {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => { setOpen(false); }, [loc.pathname]);
  const s = site.settings;
  return (
    <header className="sticky top-0 z-40 bg-white border-b border-border" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 h-20 flex items-center gap-6">
        <Link to="/" data-testid="site-logo-link" className="flex items-center gap-3 shrink-0">
          <img src={fileUrl(s.logo_url)} alt={s.brand_name} className="h-14 w-14 object-contain" />
          <span className="hidden sm:block font-display text-lg leading-none notranslate" translate="no">{s.brand_name}</span>
        </Link>
        <nav className="hidden lg:flex items-center gap-1 ml-6">
          {site.pages.map((p) => (
            <NavLink key={p.slug} to={navPath(p.slug)} end data-testid={`nav-${p.slug}`}
              className={({ isActive }) => `px-4 py-2 rounded-full text-[15px] transition-colors duration-200 ${isActive ? "bg-soft text-oceandeep font-semibold" : "text-slate-700 hover:bg-sand"}`}>
              {p.nav_label}
            </NavLink>
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-3">
          <TranslatePicker />
          <a href="/admin/login" data-testid="header-admin-login-btn"
            className="hidden md:inline-flex items-center gap-1.5 h-10 px-4 rounded-full border border-border text-sm font-semibold text-slate-700 hover:bg-sand transition-colors duration-200">
            <LogIn className="w-4 h-4" />Login
          </a>
          <WaButton href={s.whatsapp_link} label="WhatsApp" testid="header-whatsapp-btn" className="hidden md:inline-flex !py-2.5 !px-5" />
          <button className="lg:hidden p-2 rounded-full hover:bg-sand" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>
      {open && (
        <nav className="lg:hidden border-t border-border bg-white px-5 py-4 flex flex-col gap-1" data-testid="mobile-menu">
          {site.pages.map((p) => (
            <NavLink key={p.slug} to={navPath(p.slug)} end data-testid={`mobile-nav-${p.slug}`}
              className={({ isActive }) => `px-4 py-3 rounded-xl text-base ${isActive ? "bg-soft text-oceandeep font-semibold" : "text-slate-700"}`}>
              {p.nav_label}
            </NavLink>
          ))}
          <a href="/admin/login" data-testid="mobile-admin-login-btn" className="mt-2 px-4 py-3 rounded-xl text-base border border-border flex items-center gap-2 font-semibold">
            <LogIn className="w-4 h-4" />Admin login
          </a>
        </nav>
      )}
    </header>
  );
};

const Footer = ({ site }) => {
  const s = site.settings;
  return (
    <footer className="bg-ink text-slate-300 pb-24 lg:pb-0" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 grid gap-10 md:grid-cols-[auto_1fr_auto] items-start">
        <div className="bg-white rounded-3xl p-4 w-fit"><img src={fileUrl(s.logo_url)} alt={s.brand_name} className="h-24 w-24 object-contain" /></div>
        <div className="space-y-3">
          <p className="font-display text-2xl text-white">The right guide. The right boat. The right experience.</p>
          <p className="text-sm">Komodo National Park · Labuan Bajo, Flores</p>
        </div>
        <div className="space-y-3 text-sm">
          <p className="text-white font-semibold uppercase tracking-[0.2em] text-xs">Connect</p>
          <a href={`mailto:${s.email}`} className="flex items-center gap-2 hover:text-white" data-testid="footer-email"><Mail className="w-4 h-4" />{s.email}</a>
          <a href={s.whatsapp_link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-white" data-testid="footer-phone"><Phone className="w-4 h-4" />{s.phone}</a>
          <div className="flex gap-3 pt-2">
            <a href={s.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="footer-instagram" className="p-2 rounded-full bg-white/10 hover:bg-white/20"><Instagram className="w-4 h-4" /></a>
            <a href={s.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="footer-facebook" className="p-2 rounded-full bg-white/10 hover:bg-white/20"><Facebook className="w-4 h-4" /></a>
          </div>
        </div>
      </div>
      <div className="border-t border-white/10 py-6 text-center text-xs text-slate-500">© {new Date().getFullYear()} {s.brand_name}. All rights reserved.</div>
    </footer>
  );
};

export default function SiteLayout() {
  const [site, setSite] = useState(null);
  useEffect(() => { api.get("/public/site").then((r) => setSite(r.data)); }, []);
  if (!site) return <div className="min-h-screen grid place-items-center text-slate-500" data-testid="site-loading">Loading…</div>;
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header site={site} />
      <main className="flex-1"><Outlet context={site} /></main>
      <Footer site={site} />
      <a href={site.settings.whatsapp_link} target="_blank" rel="noopener noreferrer" data-testid="sticky-plan-trip-btn"
        className="lg:hidden fixed bottom-4 inset-x-4 z-40 flex items-center justify-center gap-2 rounded-full bg-wa text-ink font-semibold py-4 shadow-lg shadow-black/10">
        <WaIcon /> Plan your trip
      </a>
    </div>
  );
}
